import { GetServerSidePropsContext } from 'next'
import Image from 'next/image'
import { useTranslation } from 'next-i18next'
import { resolveLocale } from '../../lib/resolveLocale'
import { getI18nProps } from '../../lib/getStatic'
import { HeroIcons } from '../../lib/utils/heroIcons'
import { BlogPost } from '../../lib/types/Contentful/BlogPost'
import {
  documentToReactComponents,
  Options,
} from '@contentful/rich-text-react-renderer'
import { BLOCKS, INLINES, Block, Inline } from '@contentful/rich-text-types'
import React, { ReactNode } from 'react'
import {
  extractHeadingsFromRichText,
  extractText,
  slugify,
} from '../../lib/Contentful/tools'
import { contentfulClient } from '../../lib/Contentful/client'
import TableOfContents from '../../components/blog/TableOfContent'
import useLocale from '../../hooks/useLocale'
import Link from '../../components/shared/Link'
import routes from '../../config/route'
import { ArticleJsonLd, BreadcrumbJsonLd, NextSeo } from 'next-seo'
import { getExternalLinkRel } from '../../lib/utils/seo'
import { getFullPath } from '../../lib/utils/language'
import gtmService from '../../services/gtm.service'

interface BlogPostPageProps {
  post: BlogPost
  url: string
}

export const renderOptions: Options = {
  renderNode: {
    [BLOCKS.HEADING_2]: (node: Block | Inline, children: ReactNode) => {
      const text = extractText(node)
      const id = slugify(text)
      return (
        <h2 id={id} className="mb-4 mt-8 scroll-mt-20 text-xl font-bold">
          {children}
        </h2>
      )
    },
    [BLOCKS.HEADING_3]: (node: Block | Inline, children: ReactNode) => {
      const text = extractText(node)
      const id = slugify(text)
      return (
        <h3 id={id} className="mb-2 mt-6 scroll-mt-20 text-lg font-semibold">
          {children}
        </h3>
      )
    },
    [BLOCKS.PARAGRAPH]: (_, children: ReactNode) => (
      <p className="mb-4 leading-relaxed text-gray-800">{children}</p>
    ),
    [BLOCKS.UL_LIST]: (_, children: ReactNode) => (
      <ul className="mb-4 list-inside list-disc">{children}</ul>
    ),
    [BLOCKS.OL_LIST]: (_, children: ReactNode) => (
      <ol className="mb-4 list-inside list-decimal">{children}</ol>
    ),
    [BLOCKS.LIST_ITEM]: (_, children: ReactNode) => (
      <li className="ml-4">{children}</li>
    ),
    [BLOCKS.EMBEDDED_ASSET]: (node: any) => {
      const asset = node.data.target
      const file = asset?.fields?.file
      const title = asset?.fields?.title || 'Image'

      if (!file?.url) return null

      return (
        <img
          src={`https:${file.url}`}
          alt={title}
          className="my-6 max-w-full rounded shadow"
        />
      )
    },
    [INLINES.HYPERLINK]: (node: any, children: ReactNode) => (
      <a
        href={node.data.uri}
        target="_blank"
        rel={getExternalLinkRel(node.data.uri)}
        onClick={() =>
          gtmService.outboundClick({
            linkUrl: node.data.uri,
            linkLabel: extractText(node),
            linkLocation: 'blog_post_body',
          })
        }
        className="text-blue-600 underline hover:text-blue-800"
      >
        {children}
      </a>
    ),
  },
}

export default function BlogPostPage({ post, url }: BlogPostPageProps) {
  const { t } = useTranslation('blog')
  const { locale } = useLocale()
  const fields = post?.fields
  const isRenderablePost = Boolean(
    fields &&
    fields.title &&
    fields.content &&
    fields.publishedDate &&
    fields.tag?.fields?.name,
  )

  if (!isRenderablePost || !fields) {
    return (
      <div className="flex h-full grow items-center justify-center bg-dark-desktop-ornament-min bg-cover bg-center px-4 py-16">
        <div className="w-full max-w-xl rounded-card border border-white/80 bg-white/95 p-10 text-center shadow-lg backdrop-blur-sm">
          <div className="mb-4 flex justify-center">
            <span className="flex h-10 w-10 items-center justify-center rounded-full bg-sheltersBlue/10">
              <HeroIcons.QuestionMarkCircleIcon className="h-6 w-6 text-sheltersBlue" />
            </span>
          </div>
          <h1 className="mb-3 text-xl font-semibold text-gray-800">
            {t('post.notFound.title')}
          </h1>
          <p className="mb-6 text-sm text-gray-600">
            {t('post.notFound.text')}
          </p>
          <Link
            href={routes.blog}
            className="inline-block rounded-button bg-sheltersBlue px-4 py-2 text-sm font-medium text-white transition hover:bg-sheltersBlue/90"
          >
            {t('post.notFound.cta')}
          </Link>
        </div>
      </div>
    )
  }

  const headings = extractHeadingsFromRichText(fields.content)
  const formattedDate = new Intl.DateTimeFormat(locale, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  }).format(new Date(fields.publishedDate))
  const baseUrl = process.env.NEXT_PUBLIC_BASEURL || ''
  const postUrl = getFullPath(`${routes.blog}${fields.slug}/`, locale)
  const coverImageUrl = fields.coverImage?.fields?.file?.url
    ? `https:${fields.coverImage.fields.file.url}`
    : `${baseUrl}/img/logos/shelters/logo-full-x1700.png`
  const authorName = fields.author?.fields?.name || 'Shelters'

  return (
    <>
      <NextSeo
        titleTemplate={t('seo.titleSlug')}
        defaultTitle={t('seo.title')}
        title={fields.title}
        description={fields.excerpt || t('seo.description')}
      />
      <ArticleJsonLd
        type="BlogPosting"
        url={postUrl}
        title={fields.title}
        images={[coverImageUrl]}
        datePublished={fields.publishedDate}
        dateModified={post.sys.updatedAt || fields.publishedDate}
        authorName={authorName}
        publisherName="Shelters"
        publisherLogo={`${baseUrl}/img/logos/shelters/logo-full-x500.png`}
        description={fields.excerpt || t('seo.description')}
        isAccessibleForFree
      />
      <BreadcrumbJsonLd
        itemListElements={[
          {
            position: 1,
            name: 'Shelters',
            item: baseUrl || '/',
          },
          {
            position: 2,
            name: t('title'),
            item: `${baseUrl}${getFullPath(routes.blog, locale).replace(baseUrl, '')}`,
          },
          {
            position: 3,
            name: fields.title,
            item: postUrl,
          },
        ]}
      />
      <div className="flex grow flex-col">
        <section className="bg-gradient-box">
          <div className="flex w-full flex-col items-center justify-center px-5 pt-28 tablet:px-16 desktop:px-24 2xl:px-44">
            <Link
              href={routes.blog}
              locale={locale}
              onClick={() =>
                gtmService.contentEngagement({
                  contentType: 'blog',
                  interactionType: 'back_to_list',
                })
              }
              className="mb-8 inline-flex items-center gap-2 self-start rounded-full border border-white/35 bg-[linear-gradient(145deg,rgba(255,255,255,0.22)_0%,rgba(255,255,255,0.08)_100%)] px-4 py-2.5 text-sm font-medium text-white shadow-[0_10px_28px_rgba(18,15,62,0.22)] backdrop-blur-xl backdrop-saturate-150 transition hover:-translate-y-0.5 hover:border-white/50 hover:bg-white/20"
            >
              <HeroIcons.ArrowLeftIcon className="h-4 w-4" />
              {t('post.backToList')}
            </Link>
            <div className="mb-8 flex w-full flex-col items-center space-y-3 sm:mb-16">
              <span className="hidden rounded-full bg-gray-100 p-1 px-3 py-2 text-xs text-sheltersBlue sm:block">
                {fields.tag.fields.name}
              </span>
              <h1 className="text-center text-xl font-medium text-white sm:text-4xl">
                {fields.title}
              </h1>
              <div className="flex items-center space-x-6">
                <span className="flex items-center space-x-1 text-gray-100">
                  <HeroIcons.CalendarIcon className="h-4 w-4" />
                  <p className="text-xs">{formattedDate}</p>
                </span>
                <span className="flex items-center space-x-1 text-gray-100">
                  <HeroIcons.ClockIcon className="h-4 w-4" />
                  <p className="text-xs">
                    {fields.readTime} {t('post.minutes')}
                  </p>
                </span>
              </div>
            </div>
            <div className="relative z-10 -mb-32 h-80 w-full overflow-hidden rounded-card shadow-[0_24px_60px_rgba(18,15,62,0.24)] tablet:h-96 desktop:h-[420px]">
              <Image
                src={coverImageUrl}
                alt={fields.coverImage?.fields?.title || 'Blog image'}
                fill
                priority
                sizes="(min-width: 1024px) 80vw, 100vw"
                className="h-full w-full object-cover"
              />
            </div>
          </div>
        </section>

        <section className="flex w-full grow justify-center bg-slate-50 pb-8 pt-44 sm:pb-16">
          <div className="w-full px-5 tablet:px-16 desktop:px-24 2xl:px-44">
            <div className="grid w-full grid-cols-3 gap-6">
              <div className="col-span-3 sm:col-span-1">
                {fields.author && (
                  <div className="flex flex-col border-b border-gray-300 pb-3">
                    <div className="flex items-center gap-3">
                      <img
                        src={
                          fields.author.fields.photo?.fields?.file?.url
                            ? `https:${fields.author.fields.photo.fields.file.url}`
                            : ''
                        }
                        alt={
                          fields.author.fields.photo?.fields?.title ||
                          fields.author.fields.name
                        }
                        className="h-8 w-8 rounded-full object-cover"
                      />
                      <div>
                        <p className="text-sm font-medium">
                          {fields.author.fields.name}
                        </p>
                        <p className="text-xs font-normal">
                          {fields.author.fields.role}
                        </p>
                      </div>
                    </div>
                  </div>
                )}
                <div className="border-b border-gray-300 pb-3 pt-3">
                  <TableOfContents items={headings} />
                </div>
              </div>
              <div className="col-span-3 sm:col-span-2">
                {documentToReactComponents(fields.content, renderOptions)}
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  )
}

// 👉 getServerSideProps remplace getStaticProps + getStaticPaths
export async function getServerSideProps(context: GetServerSidePropsContext) {
  const slug = context.params?.slug as string
  const locale = resolveLocale(context)

  const entries = await contentfulClient.getEntries({
    content_type: 'blogPost',
    'fields.slug': slug,
    locale,
  })

  if (!entries.items.length) {
    return { notFound: true }
  }

  const i18nProps = await getI18nProps(context, [
    'common',
    'index',
    'blog',
    'navbar',
    'footer',
    'modals',
    'newsletter',
  ])

  if (process.env.TARGET_ENV === 'production') {
    context.res.setHeader(
      'Vercel-CDN-Cache-Control',
      'public, s-maxage=21600, stale-while-revalidate=86400',
    )
  }

  return {
    props: {
      ...i18nProps,
      post: entries.items[0],
      url: context.req.url,
    },
  }
}
